import React from 'react';
import { Helmet } from 'react-helmet';
import { PageDataType } from 'templates/types';
import { withPrefix } from 'gatsby';
import { createSlug, isHomePage, prismicText } from 'helpers';
import { PageContextType } from 'types';
const packageJSON = require('../../../package');

const PageMetadata = ({ page, pageContext }: { page: PageDataType; pageContext: PageContextType }) => {
    const {
        _meta: { alternateLanguages: alternateLanguages, lang },
        theme,
        title,
        meta_title: metaTitle,
        meta_description: metaDescription,
        meta_canonical: metaCanonical,
        meta_image: metaImage,
        meta_twitter_image: metaTwitterImage,
    } = page;

    const titleText = prismicText(metaTitle) || prismicText(title);
    const descriptionText = prismicText(metaDescription);

    // If there is an open graph image uploaded in Prismic, use that. Otherwise, fallback to the image in the
    // static/ folder
    const openGraphUrl = (metaImage && metaImage.url) || `${process.env.GATSBY_GUESTSITE_ENDPOINT}/og_image.jpg`;

    // If there is a twitter image specified in Prismic, use that, otherwise use the open graph image uploaded
    // in Prismic. If none of those images have been set, use the fallback image found in the static/ folder
    const twitterUrl =
        (metaTwitterImage && metaTwitterImage.url) ||
        (metaImage && metaImage.url) ||
        `${process.env.GATSBY_GUESTSITE_ENDPOINT}/twitter_image.jpg`;

    const canonicalUrl = isHomePage(pageContext.uid)
        ? `${process.env.GATSBY_GUESTSITE_ENDPOINT}${createSlug({
              language: lang,
              type: 'home',
          })}`
        : (metaCanonical && metaCanonical.url) ||
          `${process.env.GATSBY_GUESTSITE_ENDPOINT}${createSlug({
              language: lang,
              uid: pageContext.uid,
          })}`;

    return (
        <Helmet>
            <html className={theme} lang={lang} />
            <meta name="version" content={packageJSON.version} />
            {/* General tags */}
            <base href="/" />
            <title>{titleText}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <meta charSet="utf-8" />
            {/* Tell IE what version of IE the page should be rendered as */}
            <meta content="IE=edge,chrome=1" http-equiv="X-UA-Compatible" />
            {/* Associate a specific cryptographic public key with a certain web
            server to decrease the risk of MITM attacks with forged certificates */}
            <meta
                http-equiv="Public-Key-Pins"
                content="pin-sha256='sW+2XERgp5Lk1y2Z4EIm3nuXHcHx0ygDxnmWm8/ynSk='; max-age=2592000"
            />
            {/* Tell browser that page should only be accessed through HTTPS */}
            <meta http-equiv="Strict-Transport-Security" content="max-age=15552000; preload" />
            {/* Indicate that the MIME types advertised in the Content-Type
            headers should not be changed and be followed */}
            <meta http-equiv="X-Content-Type-Options" content="nosniff" />
            {/* Verify identity for Google Search Console */}
            <meta name="google-site-verification" content="i1YKmdWehqbFzzuhjEmdfnZmam2X_m0eTQbafHib7fU" />
            {/* Verify identity with Bing Webmaster Center */}
            <meta name="msvalidate.01" content="186B757CCC477822E5AEE78D13E890F9" />
            {/* SEO description */}
            {descriptionText && <meta name="description" content={descriptionText} />}
            {/* SEO canonical URL to indicate what page the SEO information should be pulled from.
                Used when there are two pages with the identical content and you need to tell search
                engines which page should be treated as the main page. */}
            <link rel="canonical" href={canonicalUrl} />
            {/* OpenGraph tags */}
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={titleText} />
            <meta property="og:type" content="website" />
            {descriptionText && <meta property="og:description" content={descriptionText} />}
            <meta property="og:image" content={openGraphUrl} />
            <meta property="og:site_name" content="LANDR" />
            {/* Twitter Card tags */}
            <meta name="twitter:title" content={titleText} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:creator" content="@LANDR_music" />
            {descriptionText && <meta name="twitter:description" content={descriptionText} />}
            <meta name="twitter:image" content={twitterUrl} />
            {/* Set an icon image when saving the page to an iOS homescreen. The -precomposed is appended to
            apple-touch-icon to support iOS versions 6 and under: https://stackoverflow.com/questions/20716490/apple-touch-icon-precomposed-is-not-a-real-value-for-the-rel-attribute */}
            <link rel="apple-touch-icon-precomposed" href={withPrefix('favicon-180.png')} />
            {/* Favicon */}
            <link rel="shortcut icon" href={withPrefix('favicon-32.png')} />
            {/* Language (content should take the form of en-CA) */}
            <meta http-equiv="content-language" content={lang} />
            {/* TODO: Allow a preferred language to be selected */}
            <link
                rel="alternate"
                href={
                    isHomePage(pageContext.uid)
                        ? `${process.env.GATSBY_GUESTSITE_ENDPOINT}${createSlug({
                              language: lang,
                              type: 'home',
                          })}`
                        : `${process.env.GATSBY_GUESTSITE_ENDPOINT}${createSlug({
                              language: lang,
                              uid: pageContext.uid,
                          })}`
                }
                hrefLang="x-default"
            />
            {alternateLanguages.map(item => (
                <link
                    key={item.uid}
                    rel="alternate"
                    href={
                        isHomePage(pageContext.uid)
                            ? `${process.env.GATSBY_GUESTSITE_ENDPOINT}${createSlug({
                                  language: item.lang,
                                  type: 'home',
                              })}`
                            : `${process.env.GATSBY_GUESTSITE_ENDPOINT}${createSlug({
                                  language: item.lang,
                                  uid: item.uid,
                              })}`
                    }
                    hrefLang={item.lang}
                />
            ))}
            {/* Corporate Contacts Information for Google Search */}
            <script type="application/ld+json">
                {`
                    {
                    "@context" : "http://schema.org",
                    "@type" : "Organization",
                    "name" : "LANDR",
                    "logo" : "https://www.landr.com/img/logo-landr.png",
                    "url" : "https://www.landr.com",
                    "telephone": "+1 514-840-9700",
                    "email": "mailto:support@landr.com",
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": "Montreal",
                        "addressRegion": "QC",
                        "addressCountry": "CA",
                        "postalCode":"H2T 1A8",
                        "streetAddress": "160 St. Viateur East"
                    },
                    "sameAs" : [
                        "https://twitter.com/LANDR_music",
                        "https://www.facebook.com/LANDRmusic",
                        "https://www.youtube.com/user/LANDRmastering",
                        "https://www.linkedin.com/company/mixgeni-us"
                        ]
                    }
                `}
            </script>
            {/* Unbounce Popups used by marketing */}
            {process.env.GATSBY_UNBOUNCE_URL !== 'null' && <script src={`${process.env.GATSBY_UNBOUNCE_URL}`} async />}
        </Helmet>
    );
};

export { PageMetadata };
